import { ACCOUNT_MANAGEMENT } from "@buzunarul/shared-constants";
import toast from 'react-hot-toast';
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { logWithTimestamp } from "../utils/dateHelpers";
import { AccountBalance } from "../hooks/balanceCalculator/useBalanceCalculator.types";
import { accountService } from "../services/accountService";
import { supabase } from "../services/supabase";

/**
 * 🏪 ACCOUNTSSTORE - DEDICATED ACCOUNT MANAGEMENT
 * 
 * 🎯 EXTRACTED from settingsStore (Task 5)
 * Separates account management logic into dedicated store
 * using Zustand with persistence for clean architecture.
 * 
 * 🧠 FEATURES:
 * - Account CRUD operations (add, update, delete)
 * - Remote sync with Supabase RPC
 * - Optimistic updates with offline fallback
 * - Default account management
 * - Validation and error handling
 * - Persistence with Zustand middleware
 * 
 * ⚙️ INTEGRATION:
 * - Maintains same API surface as settingsStore
 * - Uses existing AccountBalance interface
 * - Leverages shared-constants for validation
 * - Compatible with existing account services
 * 
 * 🐛 ERROR HANDLING:
 * - Network failures with fallback to local
 * - Validation errors with user feedback
 * - Retry logic for remote operations
 * - Optimistic updates for smooth UX
 */

/**
 * Account types supported by the system
 */
export type AccountType = 'checking' | 'savings' | 'investment' | 'credit' | 'cash';

/**
 * Account validation error types
 */
export type AccountValidationError = 
  | 'NAME_REQUIRED' 
  | 'NAME_TOO_LONG' 
  | 'TYPE_REQUIRED' 
  | 'INITIAL_BALANCE_REQUIRED' 
  | 'INITIAL_BALANCE_INVALID' 
  | 'DUPLICATE_NAME' 
  | 'CANNOT_DELETE_LAST' 
  | 'CANNOT_DELETE_WITH_TRANSACTIONS';

/**
 * State management interface for AccountsStore
 */
interface AccountsState {
  // Core data
  accounts: AccountBalance[];
  defaultAccountId: string | null;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Account CRUD operations
  addAccount: (account: Omit<AccountBalance, 'accountId'>) => Promise<void>;
  updateAccount: (accountId: string, updates: Partial<AccountBalance>) => Promise<void>;
  removeAccount: (accountId: string) => Promise<void>;
  setDefaultAccount: (accountId: string) => void;
  getAccountById: (accountId: string) => AccountBalance | null;

  // Utility actions
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // First-time setup
  initializeDefaultAccounts: () => void;
  isFirstTimeSetup: () => boolean;

  // Computed getters
  getActiveAccounts: () => AccountBalance[];
  getTotalInitialBalance: () => number;
  hasMultipleAccounts: () => boolean;

  // Remote synchronization
  syncRemoteAccounts: () => Promise<void>;

  // Validation helpers
  validateAccount: (account: Partial<AccountBalance>) => AccountValidationError | null;
  isDuplicateName: (name: string, excludeAccountId?: string) => boolean;
}

/**
 * Creates default accounts for first-time setup
 * Task 1.3: Prepopulate cu 3 conturi specifice
 */
const createDefaultAccounts = (): AccountBalance[] => [
  {
    accountId: 'default-bank-account',
    accountName: 'Bancă',
    initialBalance: 1000,
    currentBalance: 1000,
    currency: 'RON',
    accountType: 'checking',
    isActive: true,
    displayOrder: 1,
    includedInTotal: true,
    iconName: 'Banknote',
  },
  {
    accountId: 'default-cash-account',
    accountName: 'Cash',
    initialBalance: 200,
    currentBalance: 200,
    currency: 'RON',
    accountType: 'cash',
    isActive: true,
    displayOrder: 2,
    includedInTotal: true,
    iconName: 'Wallet',
  },
  {
    accountId: 'default-investment-account',
    accountName: 'Investiții',
    initialBalance: 0,
    currentBalance: 0,
    currency: 'RON',
    accountType: 'investment',
    isActive: true,
    displayOrder: 3,
    includedInTotal: false,
    iconName: 'LineChart',
  },
];

/**
 * Helper to check if a string is a valid UUID v4
 */
const isValidUUID = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/**
 * AccountsStore implementation using Zustand with persistence
 */
export const useAccountsStore = create<AccountsState>()(
  persist(
    (set, get) => ({
      // Initial state
      accounts: [],
      defaultAccountId: null,
      isLoading: false,
      error: null,

      // Account CRUD operations
      addAccount: async (accountData) => {
        set({ isLoading: true, error: null });

        // Check max accounts limit first
        if (get().accounts.length >= ACCOUNT_MANAGEMENT.MAX_ACCOUNTS) {
          const errorMessage = ACCOUNT_MANAGEMENT.MAX_ACCOUNTS_REACHED;
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
          return;
        }

        // Validation
        const validationError = get().validateAccount(accountData);
        if (validationError) {
          const errorMessage = ACCOUNT_MANAGEMENT.VALIDATION[validationError];
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
          return;
        }

        let accountId = `local-${crypto.randomUUID?.() ?? Date.now()}`;
        let hadAPIError = false;

        // Try remote creation first if online and authenticated
        try {
          if (navigator.onLine) {
            const session = await supabase.auth.getSession();
            const userId = session.data.session?.user?.id;
            if (userId) {
              const remote = await accountService.createAccount(userId, {
                name: accountData.accountName || 'Cont fără nume',
                account_type: accountData.accountType?.toUpperCase(),
                initial_balance: accountData.initialBalance || 0,
                included_in_total: accountData.includedInTotal !== false,
                display_order: get().accounts.length + 1,
              });
              accountId = remote.id;
            } else {
              hadAPIError = true;
            }
          }
        } catch (err) {
          console.warn('[AccountsStore] addAccount remote create failed, fallback local', err);
          hadAPIError = true;

          // Offline/error toast
          if (!navigator.onLine) {
            toast('📱 Aplicația este offline - contul a fost salvat local și va fi sincronizat la reconectare', {
              icon: '⚠️',
              duration: 4000
            });
          } else {
            toast('⚠️ Eroare server - contul a fost salvat local și va fi sincronizat ulterior', {
              icon: '🔄',
              duration: 4000
            });
          }
        }

        const newAccount: AccountBalance = {
          ...accountData,
          accountId,
          displayOrder: accountData.displayOrder ?? get().accounts.length + 1,
          currentBalance: accountData.currentBalance ?? accountData.initialBalance ?? 0,
          currency: accountData.currency ?? 'RON',
          iconName: accountData.iconName ?? 'Banknote',
          accountName: accountData.accountName || 'Cont fără nume',
          accountType: accountData.accountType ?? 'checking',
          initialBalance: accountData.initialBalance ?? 0,
          isActive: accountData.isActive ?? true,
          includedInTotal: accountData.includedInTotal !== false,
        };

        // Optimistic local update
        set((state) => ({
          accounts: [...state.accounts, newAccount],
          // Set as default if it's the first account
          defaultAccountId: state.accounts.length === 0 ? accountId : state.defaultAccountId,
          error: null,
          isLoading: false,
        }));

        // Success toast (only if no API error was shown)
        if (!hadAPIError) {
          toast.success(`✅ ${ACCOUNT_MANAGEMENT.STATUS.ACCOUNT_CREATED}: "${accountData.accountName}"`);
        }
      },

      updateAccount: async (accountId, updates) => {
        set({ isLoading: true, error: null });

        // Validation for updates
        if (updates.accountName !== undefined || updates.accountType !== undefined || updates.initialBalance !== undefined) {
          const currentAccount = get().getAccountById(accountId);
          if (currentAccount) {
            const updatedAccount = { ...currentAccount, ...updates };
            const validationError = get().validateAccount(updatedAccount);
            if (validationError) {
              const errorMessage = ACCOUNT_MANAGEMENT.VALIDATION[validationError];
              set({ error: errorMessage, isLoading: false });
              toast.error(errorMessage);
              return;
            }
          }
        }

        // Optimistic update
        set((state) => ({
          accounts: state.accounts.map((account) =>
            account.accountId === accountId ? { ...account, ...updates } : account
          ),
          error: null,
        }));

        let hadAPIError = false;

        // Remote update if possible
        try {
          if (navigator.onLine && isValidUUID(accountId)) {
            const session = await supabase.auth.getSession();
            if (session.data.session?.user?.id) {
              await accountService.updateAccount(accountId, {
                name: updates.accountName,
                account_type: updates.accountType?.toUpperCase(),
                current_balance: updates.currentBalance,
                included_in_total: updates.includedInTotal,
                is_active: updates.isActive,
                display_order: updates.displayOrder,
              });
            }
          }
        } catch (err) {
          console.error('[AccountsStore] updateAccount API error', err);
          hadAPIError = true;

          // Error toast
          if (!navigator.onLine) {
            toast('📱 Modificarea salvată offline - va fi sincronizată la reconectare', {
              icon: '⚠️',
              duration: 3000
            });
          } else {
            toast('⚠️ Eroare server - modificarea este salvată local și va fi sincronizată ulterior', {
              icon: '🔄',
              duration: 3000
            });
          }
        }

        set({ isLoading: false });

        // Success toast
        if (!hadAPIError) {
          toast.success(`✅ ${ACCOUNT_MANAGEMENT.STATUS.ACCOUNT_UPDATED}`);
        }
      },

      removeAccount: async (accountId) => {
        set({ isLoading: true, error: null });
        const state = get();

        // Validate: cannot delete last active account
        const activeAccounts = state.accounts.filter(acc => acc.isActive);
        if (activeAccounts.length === 1 && activeAccounts[0]?.accountId === accountId) {
          const errorMessage = ACCOUNT_MANAGEMENT.VALIDATION.CANNOT_DELETE_LAST;
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
          return;
        }

        const accountToDelete = state.getAccountById(accountId);
        const accountName = accountToDelete?.accountName || 'contul';

        // Optimistic delete
        set((state) => {
          const newAccounts = state.accounts.filter(acc => acc.accountId !== accountId);
          return {
            accounts: newAccounts,
            defaultAccountId: state.defaultAccountId === accountId
              ? newAccounts.find(acc => acc.isActive)?.accountId || null
              : state.defaultAccountId,
            error: null,
          };
        });

        // Remote delete
        try {
          if (navigator.onLine && isValidUUID(accountId)) {
            await accountService.deleteAccount(accountId);
          }
        } catch (err) {
          console.error('[AccountsStore] removeAccount API error', err);
          toast.error('⚠️ Eroare server – contul a fost eliminat doar local');
        }

        set({ isLoading: false });
        toast.success(ACCOUNT_MANAGEMENT.TOASTS.DELETE_SUCCESS(accountName));
      },

      setDefaultAccount: (accountId) => {
        const account = get().getAccountById(accountId);
        if (!account) {
          const errorMessage = 'Contul nu a fost găsit';
          set({ error: errorMessage });
          toast.error(errorMessage);
          return;
        }

        if (!account.isActive) {
          const errorMessage = 'Contul nu este activ';
          set({ error: errorMessage });
          toast.error(errorMessage);
          return;
        }

        set({ defaultAccountId: accountId, error: null });
        toast.success(`✅ ${ACCOUNT_MANAGEMENT.STATUS.ACCOUNT_SET_DEFAULT}: "${account.accountName}"`);
      },

      getAccountById: (accountId) => {
        return get().accounts.find(acc => acc.accountId === accountId) || null;
      },

      // Utility actions
      reset: () => {
        set({
          accounts: createDefaultAccounts(),
          defaultAccountId: 'default-bank-account',
          isLoading: false,
          error: null,
        });
        logWithTimestamp('AccountsStore reset to default accounts');
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      // First-time setup
      initializeDefaultAccounts: () => {
        const defaultAccounts = createDefaultAccounts();
        set({
          accounts: defaultAccounts,
          defaultAccountId: 'default-bank-account',
          error: null,
        });
        logWithTimestamp('AccountsStore initialized with default accounts');
      },

      isFirstTimeSetup: () => {
        const state = get();
        return (
          state.accounts.length === 0 ||
          (state.accounts.length === 1 &&
           state.accounts[0]?.accountId === 'default-account' &&
           state.accounts[0]?.accountName === 'Cont Principal')
        );
      },

      // Computed getters
      getActiveAccounts: () => {
        return get().accounts
          .filter(acc => acc.isActive)
          .sort((a, b) => a.displayOrder - b.displayOrder);
      },

      getTotalInitialBalance: () => {
        return get().accounts
          .filter(acc => acc.includedInTotal !== false)
          .reduce((sum, acc) => sum + (acc.currentBalance ?? acc.initialBalance), 0);
      },

      hasMultipleAccounts: () => {
        return get().accounts.filter(acc => acc.isActive).length > 1;
      },

      // Remote synchronization
      syncRemoteAccounts: async () => {
        try {
          if (!navigator.onLine) return;
          
          const session = await supabase.auth.getSession();
          const userId = session.data.session?.user?.id;
          if (!userId) return;

          const remote = await accountService.fetchAccounts(userId);
          if (remote.length === 0) return;

          // Map remote structure to AccountBalance
          const mapped = remote.map<AccountBalance>((r) => ({
            accountId: r.id,
            accountName: r.name,
            initialBalance: r.initial_balance,
            currentBalance: r.current_balance,
            currency: 'RON', // TODO: add to API
            accountType: (r.account_type?.toLowerCase() ?? 'checking') as AccountBalance['accountType'],
            isActive: r.is_active,
            displayOrder: r.display_order ?? 0,
            includedInTotal: r.included_in_total,
            iconName: 'Banknote', // TODO: add to API
          }));

          set({ accounts: mapped, error: null });
          logWithTimestamp(`AccountsStore synced ${mapped.length} remote accounts`);
        } catch (err) {
          console.error('[AccountsStore] syncRemoteAccounts error', err);
        }
      },

      // Validation helpers
      validateAccount: (account) => {
        // Name validation
        if (!account.accountName || account.accountName.trim() === '') {
          return 'NAME_REQUIRED';
        }
        if (account.accountName.length > 50) {
          return 'NAME_TOO_LONG';
        }

        // Type validation
        if (!account.accountType) {
          return 'TYPE_REQUIRED';
        }

        // Balance validation
        if (account.initialBalance === undefined || account.initialBalance === null) {
          return 'INITIAL_BALANCE_REQUIRED';
        }
        if (typeof account.initialBalance !== 'number' || isNaN(account.initialBalance)) {
          return 'INITIAL_BALANCE_INVALID';
        }

        // Duplicate name check (only if we have an accountId to exclude)
        if (account.accountName && get().isDuplicateName(account.accountName, account.accountId)) {
          return 'DUPLICATE_NAME';
        }

        return null;
      },

      isDuplicateName: (name, excludeAccountId) => {
        return get().accounts.some(acc => 
          acc.accountName?.toLowerCase().trim() === name.toLowerCase().trim() && 
          acc.accountId !== excludeAccountId
        );
      },
    }),
    {
      name: 'budget-app-accounts',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accounts: state.accounts,
        defaultAccountId: state.defaultAccountId,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.isFirstTimeSetup()) {
          logWithTimestamp('[AccountsStore] First-time setup detected - initializing default accounts');
          state.initializeDefaultAccounts();
        }

        // Sync with remote after rehydration
        state?.syncRemoteAccounts();
      },
    }
  )
);

// Export selectori pentru performance optimizat
export const useAccountsLoading = () => useAccountsStore((state) => state.isLoading);
export const useAccountsError = () => useAccountsStore((state) => state.error);
export const useAccounts = () => useAccountsStore((state) => state.accounts);
export const useActiveAccounts = () => useAccountsStore((state) => state.getActiveAccounts());
export const useDefaultAccountId = () => useAccountsStore((state) => state.defaultAccountId); 